import type { CampaignSchedule } from '@k2/domain/campaign-plan'
import { readDocumentSection } from '@k2/domain/profile-document'
import { defineStore } from 'pinia'
import { type Ref, ref } from 'vue'
import { useApiRequest } from '@/api'
import { useCampaignsResource } from '../resources'
import { useCampaignStore } from './campaign.store'

// The writes the Campaign brief panel makes by hand, and the section being edited while the agent may write it too.
export const useBriefStore = defineStore('campaign-brief', () => {
  const { setCampaignName, setSchedule, setSignOff, setBriefSection, setBriefField } = useCampaignsResource()
  const campaignStore = useCampaignStore()
  const { runScoped } = useApiRequest()

  const saving = ref(false)
  const signingOff = ref(false)
  const failures = ref<Record<string, string>>({})
  const editing = ref<{ readonly heading: string; readonly base: string } | null>(null)
  // A section the agent rewrote while the user was editing it: the agent's version, until the user picks.
  const offered = ref<Record<string, string>>({})

  const write = async (
    flag: Ref<boolean>,
    call: () => Promise<{ data: unknown; error: unknown }>,
    organizationId: string,
    campaignId: string
  ) => {
    if (flag.value) return false
    flag.value = true
    const { [campaignId]: _, ...others } = failures.value
    failures.value = others
    try {
      const request = runScoped(call)
      const written = await request.result
      const message = request.failure.value?.message
      if (!written && message) failures.value = { ...failures.value, [campaignId]: message }
      await campaignStore.loadCampaign(organizationId, campaignId)
      return !!written
    } finally {
      flag.value = false
    }
  }

  const bodyOf = (campaignId: string, heading: string) =>
    readDocumentSection(campaignStore.campaigns[campaignId]?.briefDocument ?? '', heading)

  const rename = (organizationId: string, campaignId: string, name: string) =>
    write(saving, () => setCampaignName(organizationId, campaignId, name), organizationId, campaignId)

  const reschedule = (organizationId: string, campaignId: string, schedule: CampaignSchedule) =>
    write(saving, () => setSchedule(organizationId, campaignId, schedule), organizationId, campaignId)

  const signOff = (organizationId: string, campaignId: string) =>
    write(signingOff, () => setSignOff(organizationId, campaignId), organizationId, campaignId)

  const saveSection = (organizationId: string, campaignId: string, heading: string, body: string) =>
    write(saving, () => setBriefSection(organizationId, campaignId, heading, body), organizationId, campaignId)

  const saveField = (organizationId: string, campaignId: string, field: 'insight' | 'proposition', value: string) =>
    write(saving, () => setBriefField(organizationId, campaignId, field, value), organizationId, campaignId)

  const startEdit = (campaignId: string, heading: string) => {
    editing.value = { heading, base: bodyOf(campaignId, heading) }
  }

  const stopEdit = () => {
    editing.value = null
  }

  const agentWrote = (campaignId: string, heading: string) =>
    editing.value?.heading === heading && bodyOf(campaignId, heading) !== editing.value.base

  const finishEdit = async (organizationId: string, campaignId: string, draft: string) => {
    const edit = editing.value
    if (!edit) return
    const theirs = bodyOf(campaignId, edit.heading)
    if (theirs !== edit.base) offered.value = { ...offered.value, [edit.heading]: theirs }
    if (await saveSection(organizationId, campaignId, edit.heading, draft.trim())) editing.value = null
  }

  const settle = (heading: string) => {
    const { [heading]: _, ...rest } = offered.value
    offered.value = rest
  }

  const useOffered = async (organizationId: string, campaignId: string, heading: string) => {
    const theirs = offered.value[heading]
    if (theirs !== undefined && (await saveSection(organizationId, campaignId, heading, theirs))) settle(heading)
  }

  return {
    saving,
    signingOff,
    failures,
    editing,
    offered,
    rename,
    reschedule,
    signOff,
    saveSection,
    saveField,
    startEdit,
    stopEdit,
    agentWrote,
    finishEdit,
    settle,
    useOffered
  }
})
